import { LitElement, html, css } from 'lit';
import './toggle-switch-element.js';

class CookieConsentPillElement extends LitElement {
  static get properties() {
    return {
      _visible: { type: Boolean, state: true },
      _showPreferences: { type: Boolean, state: true },
      _analyticsOn: { type: Boolean, state: true },
    };
  }

  constructor() {
    super();
    this._visible = false;
    this._showPreferences = false;
    this._analyticsOn = true;
    this._handleShowEvent = this._handleShowEvent.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    const cachedValue = localStorage.getItem('cookie_consent_cache');
    this._visible = cachedValue === null;
    if (cachedValue === 'true' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
      });
    }
    document.addEventListener('show-cookie-consent', this._handleShowEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('show-cookie-consent', this._handleShowEvent);
  }

  _handleShowEvent() {
    const prior = localStorage.getItem('cookie_consent_cache');
    this._analyticsOn = prior !== 'false';
    localStorage.removeItem('cookie_consent_cache');
    this._visible = true;
    this._showPreferences = false;
  }

  _accept() {
    localStorage.setItem('cookie_consent_cache', 'true');
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
      });
    }
    this._visible = false;
  }

  _reject() {
    localStorage.setItem('cookie_consent_cache', 'false');
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
      });
    }
    this._visible = false;
  }

  _confirm() {
    if (this._analyticsOn) {
      this._accept();
    } else {
      this._reject();
    }
  }

  _openPreferences() {
    this._showPreferences = true;
  }

  _closePreferences() {
    this._showPreferences = false;
  }

  _onAnalyticsToggle(e) {
    this._analyticsOn = e.detail;
  }

  static get styles() {
    return css`
      :host {
        display: block;
      }

      .pill-wrapper {
        position: fixed;
        bottom: 24px;
        right: 24px;
        z-index: 1001;
        max-width: 400px;
        width: calc(100vw - 48px);
      }

      .card {
        background: #FFFFFF;
        border-radius: 12px;
        padding: 20px 24px;
        box-shadow: 0px 4px 16px 0px rgba(108, 108, 108, 0.15);
        border: 1px solid #E7E7E7;
        font-family: Outfit, sans-serif;
      }

      .title {
        font-size: 16px;
        font-weight: 700;
        color: #2E2E3A;
        margin: 0 0 10px 0;
      }

      .body-text {
        font-size: 14px;
        font-weight: 400;
        color: #535362;
        margin: 0 0 14px 0;
        line-height: 1.5;
      }

      .body-text a {
        color: #3346E7;
        text-decoration: none;
      }

      .body-text a:hover {
        text-decoration: underline;
      }

      .manage-prefs {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 13px;
        color: #3346E7;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        font-family: Outfit, sans-serif;
        margin-bottom: 16px;
      }

      .manage-prefs box-icon {
        display: inline-block;
        width: 15px;
        height: 15px;
      }

      .manage-prefs:hover {
        text-decoration: underline;
      }

      .back-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 13px;
        color: #535362;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        font-family: Outfit, sans-serif;
        margin-bottom: 14px;
      }

      .back-btn:hover {
        color: #2E2E3A;
      }

      .back-btn box-icon {
        display: inline-block;
        width: 15px;
        height: 15px;
      }

      .buttons {
        display: flex;
        gap: 10px;
      }

      .btn-reject {
        background: #FFFFFF;
        color: #2E2E3A;
        border: 1px solid #E7E7E7;
        border-radius: 8px;
        padding: 8px 16px;
        cursor: pointer;
        font-family: Outfit, sans-serif;
        font-size: 14px;
        flex: 1;
      }

      .btn-accept {
        background: #3346E7;
        color: #FFFFFF;
        border: 1px solid #3346E7;
        border-radius: 8px;
        padding: 8px 16px;
        cursor: pointer;
        font-family: Outfit, sans-serif;
        font-size: 14px;
        box-shadow: 0px 15px 15px 0px rgba(51, 70, 231, 0.15);
        flex: 1;
      }

      .pref-section {
        margin-bottom: 14px;
      }

      .pref-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 6px;
      }

      .pref-label {
        font-size: 14px;
        font-weight: 600;
        color: #2E2E3A;
      }

      .pref-desc {
        font-size: 13px;
        color: #535362;
        margin: 0 0 4px 0;
      }

      .always-active {
        font-size: 12px;
        color: #535362;
        font-style: italic;
      }

      @media (max-width: 480px) {
        .pill-wrapper {
          left: 0;
          right: 0;
          bottom: 0;
          max-width: 100%;
          width: 100%;
        }

        .card {
          border-radius: 12px 12px 0 0;
        }

        .buttons {
          flex-direction: column;
        }
      }
    `;
  }

  render() {
    if (!this._visible) return html``;

    return html`
      <div class="pill-wrapper">
        <div class="card">
          ${this._showPreferences ? this._renderPreferences() : this._renderDefault()}
        </div>
      </div>
    `;
  }

  _renderDefault() {
    return html`
      <p class="title">You are in control of your cookies</p>
      <p class="body-text">
        We use analytics to improve your experience. View our
        <a href="/privacy">Privacy Policy</a>.
      </p>
      <button type="button" class="manage-prefs" @click="${this._openPreferences}">
        <box-icon name='cog'></box-icon>
        Manage Preferences
      </button>
      <div class="buttons">
        <button type="button" class="btn-reject" @click="${this._reject}">Reject cookies</button>
        <button type="button" class="btn-accept" @click="${this._accept}">Accept all</button>
      </div>
    `;
  }

  _renderPreferences() {
    return html`
      <button type="button" class="back-btn" @click="${this._closePreferences}">
        <box-icon name='arrow-back'></box-icon>
        Back
      </button>
      <p class="title">Cookie Preferences</p>
      <div class="pref-section">
        <div class="pref-row">
          <span class="pref-label">Necessary</span>
          <span class="always-active">Always active</span>
        </div>
        <p class="pref-desc">Required for the app to work.</p>
      </div>
      <div class="pref-section">
        <div class="pref-row">
          <span class="pref-label">Analytics</span>
          <toggle-switch-element
            .on="${this._analyticsOn}"
            style="--toggle-color: #3346E7"
            @switch-toggled="${this._onAnalyticsToggle}"
          ></toggle-switch-element>
        </div>
        <p class="pref-desc">Helps us understand how the app is used.</p>
      </div>
      <div class="buttons">
        <button type="button" class="btn-reject" @click="${this._reject}">Reject cookies</button>
        <button type="button" class="btn-accept" @click="${this._confirm}">Confirm</button>
      </div>
    `;
  }
}

customElements.define('cookie-consent-pill-element', CookieConsentPillElement);
